// @givmo/app-admin — giving-over-time card (spec §131).
//
// Was "Donation volume: matched donations by month, year over year". Now it tells the whole story of the
// company's giving: the matched leg AND the company's own direct giving, side by side.
//
// THREE PLOTTED SERIES, and the count is a deliberate limit:
//   Matched, this year        the area + solid brand line     (`current_year_matched`)
//   Company gave, this year   a solid green line, no fill     (`current_year_company_gave`)
//   Matched, last year        the muted dashed overlay        (`prior_year_matched`)
//
// `prior_year_company_gave` is on the wire and is NOT plotted: a fourth line makes a 12-point chart
// unreadable, and the year-over-year question this card was built to answer is about the matching
// program. The methodology note states exactly which series are drawn, so nothing is implied by omission.
//
// The second series is GREEN because it has to be told apart from brand blue and from the muted gray
// dashed overlay, and must not read as a warning — amber or red on a company's own giving would imply a
// problem with it. Green here is series identity, not a status claim.
//
// WHY THE `*_matched` FIELDS AND NOT `current_year`/`prior_year`. Both pairs carry the same numbers today
// — EP-WEB-B1 re-based the legacy fields to the matched leg and added the explicit names. Reading the
// explicit ones means this card cannot silently change meaning if the legacy pair is ever re-widened to a
// union again, which is precisely the bug B1 existed to fix: the card was titled "matched" while its
// series quietly included direct company gifts.
//
// The two legs are NEVER summed here. A combined total is derivable, but this card's job is to separate
// them — and adding server-computed money client-side is how a chart and a stat card start disagreeing.
//
// PURELY PRESENTATIONAL BY DESIGN: the page owns the query and hands this section the resolved series.

import { Card, CardBody, CardHeader, EmptyState, Icon, Skeleton } from '@givmo/design-system';
import { HandHeart } from 'lucide-react';
import type { CompanyDonationVolumePoint } from '@givmo/api-client';
import { MethodologyHint } from '../../../components/methodology-hint';
import { AreaChart } from '../area-chart';
import { periodToMonth } from '../dashboard-decode';
import { InlineError, errorMessage } from './section-status';

const METHODOLOGY =
  'Dollars per calendar month (America/New_York), January through the current month. "Matched" is ' +
  'approved employee match requests; "Company gave" is your company\'s own giving — direct donations, ' +
  "fundraiser seed gifts, and card-funded gift cards. The dashed line is last year's matched total for " +
  'the same months. The two legs are shown separately and are never added together.';

/**
 * Read one leg of one month. `*_matched` is preferred over the legacy `current_year`/`prior_year` pair
 * (same basis today, but the explicit name cannot be re-widened underneath this card). A missing
 * company-gave field reads as 0 for the geometry: a month with no company giving genuinely is zero,
 * unlike a missing scalar figure, which would render as an em-dash.
 */
const matchedThisYear = (p: CompanyDonationVolumePoint) => p.current_year_matched ?? p.current_year;
const matchedLastYear = (p: CompanyDonationVolumePoint) => p.prior_year_matched ?? p.prior_year;
const gaveThisYear = (p: CompanyDonationVolumePoint) => p.current_year_company_gave ?? 0;

export function VolumeCard({
  points,
  loading,
  isError,
  error,
  onRetry,
}: {
  points: CompanyDonationVolumePoint[];
  loading: boolean;
  isError: boolean;
  error: unknown;
  onRetry: () => void;
}) {
  // Whether the company gave anything at all this year. With no direct giving (the state until the
  // backend's COMPANY_FUNDING_SOURCE_ROUTING_ENABLED flips) a flat zero line and a legend entry for it
  // are noise, so the second series and its swatch are data-driven rather than always-on.
  const hasCompanyGiving = points.some((p) => gaveThisYear(p) > 0);

  return (
    <Card className="flex flex-col lg:col-span-2">
      {/* Below `sm` the header STACKS and the legend wraps: three entries and a hint are wider than a
          phone's header, and beside the title they squeezed it to nothing and the subtitle to a word
          a line — a live layout defect in the portal, fixed at the Donations mount (EP-MNT4). From
          `sm` up the header is the card's own row, unchanged. */}
      <CardHeader
        className="max-sm:flex-col max-sm:items-stretch"
        title="Giving over time"
        subtitle={
          hasCompanyGiving
            ? 'Matched requests and company giving by month'
            : 'Matched donations by month, year over year'
        }
        actions={
          <div className="flex flex-wrap items-center gap-x-12 gap-y-6">
            <LegendSwatch className="bg-brand-500" label="Matched" />
            {hasCompanyGiving && <LegendSwatch className="bg-success-500" label="Company gave" />}
            <LegendSwatch className="bg-text-tertiary" label="Matched, last year" dashed />
            <MethodologyHint subject="Giving over time" testId="volume-methodology">
              {METHODOLOGY}
            </MethodologyHint>
          </div>
        }
      />
      <CardBody className="flex-1 min-h-0">
        {loading ? (
          <Skeleton className="h-full min-h-[240px] w-full rounded-lg" />
        ) : isError ? (
          <InlineError
            message={errorMessage(error, 'Failed to load donation volume.')}
            onRetry={onRetry}
          />
        ) : points.length === 0 ? (
          <EmptyState
            icon={<Icon icon={HandHeart} size={20} aria-hidden />}
            title="No donation volume yet"
            description="Matched donations and company giving will chart here once your program has activity."
          />
        ) : (
          <AreaChart
            data={points.map(matchedThisYear)}
            comparison={points.map(matchedLastYear)}
            secondary={hasCompanyGiving ? points.map(gaveThisYear) : undefined}
            labels={points.map((p) => periodToMonth(p.period))}
            ariaLabel={
              hasCompanyGiving
                ? "Matched donations and company giving by month, with last year's matched total for comparison"
                : 'Matched donation volume by month, this year versus last year'
            }
            className="h-full min-h-[240px] w-full"
            height={240}
          />
        )}
      </CardBody>
    </Card>
  );
}

function LegendSwatch({
  className,
  label,
  dashed = false,
}: {
  className: string;
  label: string;
  dashed?: boolean;
}) {
  return (
    <span className="inline-flex items-center gap-6">
      <span
        className={`inline-block h-2 w-12 rounded-full ${className}`}
        style={dashed ? { opacity: 0.7 } : undefined}
        aria-hidden
      />
      <span className="font-sans text-s12 font-w500 tracking-normal text-text-tertiary">
        {label}
      </span>
    </span>
  );
}
